import type { RtkDetailSummary, RtkPeriodPoint, TokenOptimizationDailyPoint } from '@ace-tracker/shared';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { DeveloperName } from '../settings';
import { ChartCard } from './ChartCard';
import { EfficiencyGauge, PERCENT_BANDS, type GaugeBand } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import type { SourceHealthInput } from '../health';
import { ExportMenu } from './ExportMenu';
import { SourceEmptyState } from './SourceEmptyState';

const wholeNumber = (value: number) => Math.round(value).toLocaleString();

function compactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(value));
}

/** "4m 41s", "17.1s", "300ms" — RTK's own duration style, one or two units. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/**
 * RTK reduction bands. RTK's own "efficiency meter" is a plain 0–100 % bar;
 * these thresholds reflect that typical mixed shell use lands around 15–40 %,
 * so a 22 % day reads as "Moderate" rather than "Low".
 */
export const RTK_REDUCTION_BANDS: GaugeBand[] = [
  { upTo: 10, label: 'Low' },
  { upTo: 20, label: 'Fair' },
  { upTo: 35, label: 'Moderate' },
  { upTo: 50, label: 'Good' },
  { upTo: 100, label: 'Excellent' },
];

const RECOVERY_BANDS: GaugeBand[] = [
  { upTo: 50, label: 'Poor' },
  { upTo: 70, label: 'Fair' },
  { upTo: 85, label: 'Moderate' },
  { upTo: 95, label: 'Good' },
  { upTo: 100, label: 'Excellent' },
];

type SectionHeadingProps = { title: string; hint?: string; info?: string };
type Granularity = 'daily' | 'weekly' | 'monthly';

/**
 * A day on the daily trend. A day no device reported has null measures, so
 * charts leave a gap there: "no report" is not the same as zero commands.
 */
type DailyTrendPoint = { [K in keyof Omit<TokenOptimizationDailyPoint, 'date'>]: number | null } & { date: string; reported: boolean };

const NO_REPORT = 'No report';

/** Tooltip value for a chart point: the formatted number, or "No report" for a day with no data. */
function reportedValue(value: unknown, format: (value: number) => string): string {
  return value === null || value === undefined ? NO_REPORT : format(Number(value));
}

function fillDailySeries(daily: TokenOptimizationDailyPoint[], fromIso?: string, toIso?: string): DailyTrendPoint[] {
  const reportedDays = daily.map((point) => ({ ...point, reported: true }));
  if (!fromIso || !toIso) return reportedDays;
  const start = new Date(fromIso);
  const end = new Date(toIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return reportedDays;
  const byDate = new Map(reportedDays.map((point) => [point.date, point]));
  const filled: DailyTrendPoint[] = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  for (let guard = 0; cursor <= endDay && guard < 400; guard += 1) {
    const key = cursor.toISOString().slice(0, 10);
    filled.push(
      byDate.get(key) ?? {
        date: key,
        commands: null,
        estimatedRawOutputTokens: null,
        estimatedFilteredOutputTokens: null,
        estimatedSavedTokens: null,
        reductionPercent: null,
        execTimeMs: null,
        reported: false,
      },
    );
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return filled;
}

function periodLabel(point: RtkPeriodPoint, granularity: Granularity): string {
  if (granularity === 'monthly') {
    const [year, month] = point.period.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return `${point.period.slice(5)} → ${point.periodEnd.slice(5)}`;
}

export function RtkDashboardBody({
  summary,
  SectionHeading,
  health,
}: {
  summary: RtkDetailSummary;
  SectionHeading: (props: SectionHeadingProps) => JSX.Element;
  /** RTK's data health, to explain an empty view. */
  health?: SourceHealthInput;
}) {
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const totals = summary.totals;
  const reporting = summary.developers.length > 0 || summary.lifetime !== null;
  const dailyFilled = fillDailySeries(summary.daily, summary.range.from, summary.range.to);
  const daysWithoutReport = dailyFilled.filter((point) => !point.reported).length;
  const avgTimePerCommand = totals.commands > 0 ? totals.execTimeMs / totals.commands : 0;
  // Timing arrived in collector 1.2.0; until a device sends it, show "not reported", never "0ms".
  const hasTiming = totals.execTimeMs > 0;
  const timingPending = 'Reported by collector 1.2.0+';
  const coverage = summary.coverage;
  const coveragePercent = coverage.activeAiDevelopers > 0 ? (coverage.rtk / coverage.activeAiDevelopers) * 100 : null;
  const lifetime = summary.lifetime;
  const failures = summary.parseFailures;
  const familySavedTotal = summary.commandFamilies.reduce((sum, family) => sum + family.estimatedSavedTokens, 0);

  const trendData =
    granularity === 'daily'
      ? dailyFilled.map((point) => ({ ...point, label: point.date.slice(5) }))
      : (granularity === 'weekly' ? summary.weekly : summary.monthly).map((point) => ({ ...point, label: periodLabel(point, granularity) }));

  if (!reporting) {
    return <SourceEmptyState source="rtk" health={health} />;
  }

  return (
    <>
      <aside className="rounded-card border border-border bg-surface-alt px-4 py-3 text-xs leading-relaxed text-text-muted">
        <p className="font-medium text-text-default">How to read these figures</p>
        <p className="mt-1">
          RTK filters shell-command output before it reaches the AI assistant and estimates tokens as output bytes ÷ 4. "Raw
          output" is what the command printed, "filtered output" what RTK passed on, and "saved" the difference. These are
          shell-output estimates — not model input tokens, prompt savings, or billing.
        </p>
      </aside>

      <section aria-labelledby="rtk-efficiency-heading">
        <div id="rtk-efficiency-heading">
          <SectionHeading title="Efficiency" hint="Selected range, all-time, and reliability at a glance" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <EfficiencyGauge
            label="Efficiency (selected range)"
            value={totals.estimatedRawOutputTokens > 0 ? totals.reductionPercent : null}
            bands={RTK_REDUCTION_BANDS}
            meta={`${compactNumber(totals.estimatedSavedTokens)} of ${compactNumber(totals.estimatedRawOutputTokens)} raw output tokens saved`}
            info="Estimated saved tokens ÷ estimated raw shell output, over the selected range — the same figure as RTK's own efficiency meter, for this period."
          />
          <EfficiencyGauge
            label="Efficiency (all-time)"
            value={lifetime && lifetime.estimatedRawOutputTokens > 0 ? lifetime.reductionPercent : null}
            bands={RTK_REDUCTION_BANDS}
            meta={lifetime ? `${compactNumber(lifetime.estimatedSavedTokens)} saved · ${wholeNumber(lifetime.devices)} devices · as of ${lifetime.asOfDate}` : 'Needs collector 1.2.0 lifetime snapshot'}
            info="RTK's lifetime totals (rtk gain) from each device's latest snapshot, summed across devices. Not limited to the 90-day daily history."
          />
          <EfficiencyGauge
            label="Parse recovery rate"
            value={failures ? failures.recoveryPercent : null}
            bands={RECOVERY_BANDS}
            meta={failures ? `${wholeNumber(failures.failures)} parse failures across ${wholeNumber(failures.devices)} devices` : 'Needs collector 1.2.0 lifetime snapshot'}
            info="From rtk gain --failures: commands RTK could not filter fell back to raw execution; the recovery rate is the share RTK still handled. Weighted by each device's failure count."
          />
          <EfficiencyGauge
            label="RTK coverage"
            value={coveragePercent}
            bands={PERCENT_BANDS}
            valueLabel={coveragePercent === null ? undefined : `${Math.round(coveragePercent)}%`}
            meta={coverage.activeAiDevelopers > 0 ? `${coverage.rtk} of ${coverage.activeAiDevelopers} active AI developers` : 'No active AI developers in this range'}
            info="Active AI developers (at least one Copilot, Claude Code, or Codex request in the range) who also reported RTK."
          />
        </div>
      </section>

      <section aria-labelledby="rtk-range-heading">
        <div id="rtk-range-heading">
          <SectionHeading title="Selected range" hint="Summed from RTK's daily history" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <KpiCard label="Estimated tokens saved" value={compactNumber(totals.estimatedSavedTokens)} info="RTK's byte-derived estimate. Not model input tokens or billing savings." />
          <KpiCard label="Commands" value={compactNumber(totals.commands)} meta={`${wholeNumber(summary.daily.length)} active days`} />
          <KpiCard label="Estimated raw output" value={compactNumber(totals.estimatedRawOutputTokens)} info="Shell output RTK received, estimated from byte counts." />
          <KpiCard label="Estimated filtered output" value={compactNumber(totals.estimatedFilteredOutputTokens)} info="Shell output RTK passed on to the assistant after filtering." />
          <KpiCard label="Total execution time" value={hasTiming ? formatDuration(totals.execTimeMs) : '—'} meta={hasTiming ? undefined : timingPending} info="Wall-clock time of RTK-wrapped commands, as RTK recorded it. Days sent by collectors older than 1.2.0 count as zero." />
          <KpiCard label="Avg time per command" value={hasTiming ? formatDuration(avgTimePerCommand) : '—'} meta={hasTiming ? undefined : timingPending} />
        </div>
      </section>

      {lifetime && (
        <section aria-labelledby="rtk-lifetime-heading">
          <div id="rtk-lifetime-heading">
            <SectionHeading
              title="All-time"
              hint={`Latest lifetime snapshot per device · as of ${lifetime.asOfDate}`}
              info="RTK's global lifetime totals from each device's most recent snapshot reported in the selected range, summed across devices."
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard label="Lifetime tokens saved" value={compactNumber(lifetime.estimatedSavedTokens)} meta={`${lifetime.reductionPercent.toFixed(1)}% reduction`} />
            <KpiCard label="Lifetime commands" value={compactNumber(lifetime.commands)} />
            <KpiCard label="Lifetime raw output" value={compactNumber(lifetime.estimatedRawOutputTokens)} meta={`${compactNumber(lifetime.estimatedFilteredOutputTokens)} filtered`} />
            <KpiCard label="Lifetime execution time" value={formatDuration(lifetime.execTimeMs)} meta={lifetime.commands > 0 ? `avg ${formatDuration(lifetime.execTimeMs / lifetime.commands)} per command` : undefined} />
            <KpiCard label="Reporting devices" value={wholeNumber(lifetime.devices)} />
          </div>
        </section>
      )}

      <section aria-labelledby="rtk-trends-heading">
        <div id="rtk-trends-heading" className="flex flex-wrap items-start justify-between gap-3">
          <SectionHeading title="Trends" hint="Daily, or rolled up by week (Monday start) and month like rtk gain --all" />
          <GranularityToggle value={granularity} onChange={setGranularity} />
        </div>
        {granularity === 'daily' && daysWithoutReport > 0 && (
          <p role="note" className="mb-3 text-xs text-text-muted">
            <i aria-hidden="true" className="fa-solid fa-circle-info mr-1.5 text-info" />
            {daysWithoutReport} of {dailyFilled.length} days have no RTK report. They are shown as gaps, not as zero.
          </p>
        )}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <ChartCard
            title="Tokens saved and reduction"
            caption="Bars: estimated tokens saved. Line: reduction % for the period."
            height="h-[340px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 4 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="label" {...chartXAxisTickProps} />
                <YAxis yAxisId="tokens" {...chartAxisTickProps} width={56} tickFormatter={(v: number) => compactNumber(Number(v))} />
                <YAxis yAxisId="percent" orientation="right" {...chartAxisTickProps} width={44} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip
                  {...chartTooltipProps}
                  formatter={(value, name) => [reportedValue(value, (v) => (name === 'Reduction' ? `${v.toFixed(1)}%` : wholeNumber(v))), name]}
                />
                <Legend {...chartLegendProps} />
                <Bar yAxisId="tokens" dataKey="estimatedSavedTokens" name="Estimated tokens saved" fill="#0058AB" radius={[3, 3, 0, 0]} />
                <Line yAxisId="percent" dataKey="reductionPercent" name="Reduction" stroke="#F4845F" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Raw vs filtered output" caption="What commands printed vs. what RTK passed on to the assistant." height="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 4 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="label" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} width={56} tickFormatter={(v: number) => compactNumber(Number(v))} />
                <Tooltip {...chartTooltipProps} formatter={(value, name) => [reportedValue(value, wholeNumber), name]} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="estimatedRawOutputTokens" name="Raw output" fill="#99BCDD" radius={[3, 3, 0, 0]} />
                <Bar dataKey="estimatedFilteredOutputTokens" name="Filtered output" fill="#0058AB" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Commands" caption={`${wholeNumber(totals.commands)} commands in the selected range`} height="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 4 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="label" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} width={56} tickFormatter={(v: number) => compactNumber(Number(v))} />
                <Tooltip {...chartTooltipProps} formatter={(value) => [reportedValue(value, wholeNumber), 'Commands']} />
                <Bar dataKey="commands" name="Commands" fill="#06D6A0" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Execution time" caption="Wall-clock time RTK recorded for wrapped commands." height="h-[300px]">
            {!hasTiming ? (
              <div className="flex h-full items-center justify-center text-sm text-text-muted">No timing reported yet — {timingPending.toLowerCase()}.</div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 4 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="label" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} width={64} tickFormatter={(v: number) => formatDuration(Number(v))} />
                <Tooltip {...chartTooltipProps} formatter={(value) => [reportedValue(value, formatDuration), 'Execution time']} />
                <Bar dataKey="execTimeMs" name="Execution time" fill="#C77DFF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </section>

      <section aria-labelledby="rtk-commands-heading">
        <div id="rtk-commands-heading">
          <SectionHeading
            title="By command"
            hint={summary.commandFamiliesAsOfDate ? `RTK's top commands, grouped by family · as of ${summary.commandFamiliesAsOfDate}` : "RTK's top commands, grouped by family"}
            info="From RTK's top-10 command table (rtk gain), reduced by the collector to allow-listed families such as 'rtk git diff' — arguments and paths are never sent. Lifetime figures from each device's latest snapshot. Opt-in via collection.agents.rtk.includeCommandBreakdown."
          />
        </div>
        {summary.commandFamilies.length === 0 ? (
          <div className="rounded-card border border-dashed border-border bg-surface p-5 text-sm">
            <p className="font-medium text-text-default">Command breakdown not collected</p>
            <p className="mt-1 text-text-muted">
              Set <code className="font-mono">collection.agents.rtk.includeCommandBreakdown</code> to true and add the
              optimization.rtk.command.* metric names to the allow list to see which commands RTK saves the most on.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <ChartCard title="Tokens saved by command family" caption="Lifetime, from RTK's top-command table." height="h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.commandFamilies} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="var(--line-soft)" horizontal={false} />
                  <XAxis type="number" {...chartAxisTickProps} tickFormatter={(v: number) => compactNumber(Number(v))} />
                  <YAxis type="category" dataKey="family" {...chartAxisTickProps} width={110} />
                  <Tooltip {...chartTooltipProps} formatter={(value) => [wholeNumber(Number(value)), 'Estimated tokens saved']} />
                  <Bar dataKey="estimatedSavedTokens" name="Estimated tokens saved" fill="#0058AB" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
              <ExportMenu title="Command families" className="float-right -mr-2 -mt-2 ml-2" />
              <h3 className="text-sm font-medium text-text-default">Command families</h3>
              <p className="mt-1 text-xs text-text-muted">
                Impact is each family's share of the saved tokens in this table. RTK lists only its top 10 commands, so smaller
                families may be missing.
              </p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                    <tr>
                      <th className="pb-2.5 pr-3">Command</th>
                      <th className="pb-2.5 pr-3 text-right">Count</th>
                      <th className="pb-2.5 pr-3 text-right">Saved</th>
                      <th className="pb-2.5 pr-3 text-right">Avg %</th>
                      <th className="pb-2.5 pr-3 text-right">Avg time</th>
                      <th className="pb-2.5 text-right">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.commandFamilies.map((family) => {
                      const impact = familySavedTotal > 0 ? (family.estimatedSavedTokens / familySavedTotal) * 100 : 0;
                      return (
                        <tr key={family.family} className="border-b border-border last:border-0">
                          <td className="py-2.5 pr-3 font-mono text-xs">{family.family}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums">{wholeNumber(family.commands)}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums font-medium">{compactNumber(family.estimatedSavedTokens)}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums">{family.savingsPercent.toFixed(1)}%</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums">{formatDuration(family.avgTimeMs)}</td>
                          <td className="py-2.5 text-right">
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2 w-16 overflow-hidden rounded-full bg-surface-alt" aria-hidden="true">
                                <span className="block h-full rounded-full bg-primary-blue" style={{ width: `${impact}%` }} />
                              </span>
                              <span className="w-10 text-right text-xs tabular-nums text-text-muted">{Math.round(impact)}%</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>

      <section aria-labelledby="rtk-periods-heading">
        <div id="rtk-periods-heading">
          <SectionHeading title="Breakdown tables" hint="The same periods as rtk gain --daily / --weekly / --monthly" />
        </div>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <PeriodTable title="Weekly" rows={summary.weekly} granularity="weekly" />
          <PeriodTable title="Monthly" rows={summary.monthly} granularity="monthly" />
        </div>
        <div className="mt-5">
          <PeriodTable
            title="Daily"
            rows={[...summary.daily].reverse().map((point) => ({ ...point, period: point.date, periodEnd: point.date }))}
            granularity="daily"
            scroll
          />
        </div>
      </section>

      <section aria-labelledby="rtk-developers-heading">
        <div id="rtk-developers-heading">
          <SectionHeading title="By developer" hint="Selected range from daily history; all-time from lifetime snapshots" />
        </div>
        <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
          <ExportMenu title="RTK by developer" className="-mr-2 -mt-2 mb-1 justify-end" />
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Developer</th>
                  <th className="pb-2.5 pr-3 text-right">Commands</th>
                  <th className="pb-2.5 pr-3 text-right">Saved (range)</th>
                  <th className="pb-2.5 pr-3 text-right">Reduction</th>
                  <th className="pb-2.5 pr-3 text-right">Exec time</th>
                  <th className="pb-2.5 pr-3 text-right">Saved (all-time)</th>
                  <th className="pb-2.5 pr-3 text-right">All-time reduction</th>
                  <th className="pb-2.5 pr-3 text-right">Parse recovery</th>
                  <th className="pb-2.5 text-right">Devices</th>
                </tr>
              </thead>
              <tbody>
                {developerRows(summary).map((row) => (
                  <tr key={row.developerId} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-3"><DeveloperName developerId={row.developerId} /></td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.range ? wholeNumber(row.range.commands) : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-medium">{row.range ? wholeNumber(row.range.estimatedSavedTokens) : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.range ? `${row.range.reductionPercent.toFixed(1)}%` : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.range && row.range.execTimeMs > 0 ? formatDuration(row.range.execTimeMs) : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.lifetime ? wholeNumber(row.lifetime.estimatedSavedTokens) : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.lifetime ? `${row.lifetime.reductionPercent.toFixed(1)}%` : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.lifetime?.recoveryPercent != null ? `${row.lifetime.recoveryPercent.toFixed(1)}%` : '—'}</td>
                    <td className="py-2.5 text-right tabular-nums">{Math.max(row.range?.peakDailyDevices ?? 0, row.lifetime?.devices ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}

function developerRows(summary: RtkDetailSummary) {
  const ids = [...new Set([...summary.developers.map((row) => row.developerId), ...summary.lifetimeByDeveloper.map((row) => row.developerId)])];
  return ids.map((developerId) => ({
    developerId,
    range: summary.developers.find((row) => row.developerId === developerId),
    lifetime: summary.lifetimeByDeveloper.find((row) => row.developerId === developerId),
  }));
}

function GranularityToggle({ value, onChange }: { value: Granularity; onChange: (value: Granularity) => void }) {
  const options: Granularity[] = ['daily', 'weekly', 'monthly'];
  return (
    <div role="group" aria-label="Trend granularity" className="inline-flex rounded-pill border border-border bg-surface p-0.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`min-h-[36px] rounded-pill px-4 text-xs font-medium capitalize transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue ${
            value === option ? 'bg-primary-blue text-text-inverse' : 'text-text-muted hover:bg-surface-alt hover:text-text-default'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function PeriodTable({ title, rows, granularity, scroll }: { title: string; rows: RtkPeriodPoint[]; granularity: Granularity; scroll?: boolean }) {
  const total = rows.reduce(
    (acc, row) => ({
      commands: acc.commands + row.commands,
      raw: acc.raw + row.estimatedRawOutputTokens,
      filtered: acc.filtered + row.estimatedFilteredOutputTokens,
      saved: acc.saved + row.estimatedSavedTokens,
      time: acc.time + row.execTimeMs,
    }),
    { commands: 0, raw: 0, filtered: 0, saved: 0, time: 0 },
  );
  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title={title} className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="text-sm font-medium text-text-default">{title}</h3>
      <div className={`mt-4 overflow-x-auto ${scroll ? 'max-h-[420px] overflow-y-auto' : ''}`}>
        {rows.length === 0 ? (
          <p className="text-sm text-text-muted">No RTK activity in this range.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 border-b border-border bg-surface text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-3">{granularity === 'daily' ? 'Date' : granularity === 'weekly' ? 'Week' : 'Month'}</th>
                <th className="pb-2.5 pr-3 text-right">Cmds</th>
                <th className="pb-2.5 pr-3 text-right">Raw</th>
                <th className="pb-2.5 pr-3 text-right">Filtered</th>
                <th className="pb-2.5 pr-3 text-right">Saved</th>
                <th className="pb-2.5 pr-3 text-right">Save %</th>
                <th className="pb-2.5 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.period} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 tabular-nums">{granularity === 'daily' ? row.period : periodLabel(row, granularity)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{wholeNumber(row.commands)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{compactNumber(row.estimatedRawOutputTokens)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{compactNumber(row.estimatedFilteredOutputTokens)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">{compactNumber(row.estimatedSavedTokens)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{row.reductionPercent.toFixed(1)}%</td>
                  <td className="py-2 text-right tabular-nums">{row.execTimeMs > 0 ? formatDuration(row.execTimeMs) : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-border font-medium">
              <tr>
                <td className="pt-2.5 pr-3">Total</td>
                <td className="pt-2.5 pr-3 text-right tabular-nums">{wholeNumber(total.commands)}</td>
                <td className="pt-2.5 pr-3 text-right tabular-nums">{compactNumber(total.raw)}</td>
                <td className="pt-2.5 pr-3 text-right tabular-nums">{compactNumber(total.filtered)}</td>
                <td className="pt-2.5 pr-3 text-right tabular-nums">{compactNumber(total.saved)}</td>
                <td className="pt-2.5 pr-3 text-right tabular-nums">{total.raw > 0 ? ((total.saved / total.raw) * 100).toFixed(1) : '0.0'}%</td>
                <td className="pt-2.5 text-right tabular-nums">{total.time > 0 ? formatDuration(total.time) : '—'}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
